// /app/frontend/src/components/compliance/map/ExportPanel.jsx
import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Checkbox } from '../../ui/checkbox';
import { 
  Download, Image, FileText, FileJson, Loader2, CheckCircle2
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { toPng, toSvg } from 'html-to-image';
import { toast } from 'sonner';

const EXPORT_FORMATS = [
  { 
    value: 'png', 
    label: 'PNG Image', 
    description: 'High-quality image for reports',
    icon: Image,
    extension: '.png'
  },
  { 
    value: 'svg', 
    label: 'SVG Vector', 
    description: 'Scalable vector for editing',
    icon: Image,
    extension: '.svg'
  },
  { 
    value: 'json', 
    label: 'JSON Data', 
    description: 'Raw data for backup/import',
    icon: FileJson,
    extension: '.json'
  },
  { 
    value: 'pdf', 
    label: 'PDF Report', 
    description: 'Professional document with details',
    icon: FileText,
    extension: '.pdf'
  },
];

const ExportPanel = ({ 
  open, 
  onOpenChange, 
  nodes, 
  edges,
  flowRef 
}) => {
  const [format, setFormat] = useState('png');
  const [includeDetails, setIncludeDetails] = useState(true);
  const [includeTimestamp, setIncludeTimestamp] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);

  const downloadFile = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  };

  const exportToPng = useCallback(async () => {
    if (!flowRef?.current) return;
    
    const element = flowRef.current.querySelector('.react-flow__viewport');
    if (!element) return;
    
    try {
      const dataUrl = await toPng(element, {
        backgroundColor: '#0f0a1f', // Deep purple background
        quality: 1,
        pixelRatio: 2,
      });
      
      const timestamp = includeTimestamp ? `_${new Date().toISOString().split('T')[0]}` : '';
      downloadFile(dataUrl, `data-flow-map${timestamp}.png`);
      return true;
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  }, [flowRef, includeTimestamp]);

  const exportToSvg = useCallback(async () => {
    if (!flowRef?.current) return;
    
    const element = flowRef.current.querySelector('.react-flow__viewport');
    if (!element) return;
    
    try {
      const dataUrl = await toSvg(element, {
        backgroundColor: '#0f0a1f',
      });
      
      const timestamp = includeTimestamp ? `_${new Date().toISOString().split('T')[0]}` : '';
      downloadFile(dataUrl, `data-flow-map${timestamp}.svg`);
      return true;
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  }, [flowRef, includeTimestamp]);

  const exportToJson = useCallback(() => {
    const data = {
      exportedAt: new Date().toISOString(),
      version: '1.0',
      nodes: nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          // Include all node details if selected
          ...(includeDetails ? {} : { 
            description: undefined,
            securityMeasures: undefined,
            riskFactors: undefined 
          })
        }
      })),
      edges: edges,
      metadata: {
        totalNodes: nodes.length,
        totalEdges: edges.length,
        nodeTypes: [...new Set(nodes.map(n => n.data.type))],
        highRiskNodes: nodes.filter(n => n.data.risk === 'high').length,
      }
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const timestamp = includeTimestamp ? `_${new Date().toISOString().split('T')[0]}` : '';
    downloadFile(url, `data-flow-map${timestamp}.json`);
    URL.revokeObjectURL(url);
    return true;
  }, [nodes, edges, includeDetails, includeTimestamp]);

  const exportToPdf = useCallback(async () => {
    // For PDF, we'll generate an HTML report and use print
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Data Flow Map Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #7c3aed; border-bottom: 2px solid #7c3aed; padding-bottom: 10px; }
          h2 { color: #6b7280; margin-top: 30px; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 20px 0; }
          .stat { background: #f3f4f6; padding: 20px; border-radius: 8px; text-align: center; }
          .stat-value { font-size: 32px; font-weight: bold; color: #7c3aed; }
          .stat-label { color: #6b7280; font-size: 14px; }
          .node { border: 1px solid #e5e7eb; padding: 15px; margin: 10px 0; border-radius: 8px; }
          .node-header { display: flex; justify-content: space-between; align-items: center; }
          .node-type { background: #e0e7ff; color: #4338ca; padding: 2px 8px; border-radius: 4px; font-size: 12px; }
          .risk-high { background: #fee2e2; color: #dc2626; }
          .risk-medium { background: #fef3c7; color: #d97706; }
          .risk-low { background: #d1fae5; color: #059669; }
          .categories { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
          .category { background: #ede9fe; color: #7c3aed; padding: 2px 8px; border-radius: 4px; font-size: 12px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Data Flow Map Report</h1>
        <p>Generated on ${new Date().toLocaleString()}</p>
        
        <div class="summary">
          <div class="stat">
            <div class="stat-value">${nodes.length}</div>
            <div class="stat-label">Total Nodes</div>
          </div>
          <div class="stat">
            <div class="stat-value">${edges.length}</div>
            <div class="stat-label">Connections</div>
          </div>
          <div class="stat">
            <div class="stat-value">${nodes.filter(n => n.data.risk === 'high').length}</div>
            <div class="stat-label">High Risk</div>
          </div>
          <div class="stat">
            <div class="stat-value">${nodes.filter(n => n.data.type === 'transfer').length}</div>
            <div class="stat-label">Transfers</div>
          </div>
        </div>
        
        <h2>Data Flow Nodes</h2>
        ${nodes.map(node => `
          <div class="node">
            <div class="node-header">
              <div>
                <span class="node-type">${node.data.type}</span>
                <strong style="margin-left: 10px;">${node.data.label}</strong>
              </div>
              <span class="node-type risk-${node.data.risk}">${node.data.risk} risk</span>
            </div>
            ${node.data.description ? `<p style="color: #6b7280; margin: 10px 0;">${node.data.description}</p>` : ''}
            ${node.data.dataCategories?.length ? `
              <div class="categories">
                ${node.data.dataCategories.map(cat => `<span class="category">${cat}</span>`).join('')}
              </div>
            ` : ''}
            ${node.data.legalBasis ? `<p style="margin-top: 10px;"><strong>Legal Basis:</strong> ${node.data.legalBasis}</p>` : ''}
            ${node.data.retention ? `<p><strong>Retention:</strong> ${node.data.retention}</p>` : ''}
          </div>
        `).join('')}
        
        <div class="footer">
          <p>This report was generated by Assura GRC Platform</p>
          <p>Document contains ${nodes.length} nodes and ${edges.length} data flow connections</p>
        </div>
      </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const timestamp = includeTimestamp ? `_${new Date().toISOString().split('T')[0]}` : '';
    
    // Open in new window for printing
    const printWindow = window.open(url, '_blank');
    printWindow.onload = () => {
      printWindow.print();
    };
    
    return true;
  }, [nodes, edges, includeTimestamp]);

  const handleExport = async () => {
    setExporting(true);
    setExported(false);
    
    try {
      let success = false;
      
      switch (format) {
        case 'png':
          success = await exportToPng();
          break;
        case 'svg':
          success = await exportToSvg();
          break;
        case 'json':
          success = exportToJson();
          break;
        case 'pdf':
          success = await exportToPdf();
          break;
      }
      
      if (success) {
        setExported(true);
        toast.success(`Data flow map exported as ${format.toUpperCase()}`);
        setTimeout(() => {
          setExported(false);
        }, 2000);
      }
    } catch (error) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Download className="h-5 w-5 text-violet-400" />
            Export Data Flow Map
          </DialogTitle>
          <DialogDescription>
            Choose a format and options for your export
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-300">Export Format</Label>
            <RadioGroup value={format} onValueChange={setFormat} className="space-y-2">
              {EXPORT_FORMATS.map(({ value, label, description, icon: Icon }) => (
                <div
                  key={value}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                    format === value
                      ? "border-violet-500 bg-violet-500/10"
                      : "border-slate-800 hover:border-slate-700"
                  )}
                  onClick={() => setFormat(value)}
                >
                  <RadioGroupItem value={value} />
                  <Icon className={cn(
                    "h-5 w-5",
                    format === value ? "text-violet-400" : "text-slate-500"
                  )} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{label}</p>
                    <p className="text-xs text-slate-500">{description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-300">Options</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeDetails"
                  checked={includeDetails}
                  onCheckedChange={setIncludeDetails}
                />
                <Label htmlFor="includeDetails" className="text-sm text-slate-300 cursor-pointer">
                  Include node details (descriptions, security measures)
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="includeTimestamp"
                  checked={includeTimestamp}
                  onCheckedChange={setIncludeTimestamp}
                />
                <Label htmlFor="includeTimestamp" className="text-sm text-slate-300 cursor-pointer">
                  Add timestamp to filename
                </Label>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-800">
            <p className="text-xs text-slate-500 mb-1">Export will include:</p>
            <p className="text-sm text-slate-300">
              {nodes.length} nodes • {edges.length} connections
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleExport}
            disabled={exporting}
            className={cn(
              "min-w-[120px]",
              exported 
                ? "bg-emerald-600 hover:bg-emerald-700" 
                : "bg-violet-600 hover:bg-violet-700"
            )}
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : exported ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Exported!
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportPanel;
